import { useState, useEffect, useRef } from "react";
import React from "react";
import Icon from "../components/Icon";
import ConfirmDelete from "../components/ConfirmDelete";
import PhotoGallery from "../components/PhotoGallery";
import { useProductionLogs, useSettings } from "../useFirestore";
import { usePhotos } from "../usePhotos";
import { calcPhaseDate, buildPhaseDates, extractPhasesFromEstimate, PHASE_CODES, addWorkdays, generateTruckReportHTML } from "../utils";
import TruckLog from "../components/TruckLog";

// ─── JOB COLORS (also duplicated in Schedule.jsx) ─────────────────────────────
const JOB_COLORS = ["#b91c1c","#1d4ed8","#059669","#d97706","#7c3aed","#0891b2","#be185d","#65a30d"];
function jobColor(idx) { return JOB_COLORS[idx % JOB_COLORS.length]; }

// ─── JOB BUILDER ──────────────────────────────────────────────────────────────
export function JobBuilder({ t, estimate, customer, customers, teamMembers, crews, onSave, onClose, scheduleNotes, updateScheduleNote }) {
  const today = new Date().toISOString().split("T")[0];
  const defaultCrewSize = 3;
  const defaultHoursPerDay = 9;

  const [step, setStep] = useState(1);
  const [jobInfo, setJobInfo] = useState({
    title: estimate.title || "",
    customerId: estimate.customerId || "",
    startDate: today,
    hoursPerDay: defaultHoursPerDay,
    notes: "",
    crewId: "",
    fixedDate: false,
  });
  const [phases, setPhases] = useState(() =>
    extractPhasesFromEstimate(estimate, defaultCrewSize, defaultHoursPerDay)
  );
  const [mergeSelected, setMergeSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [replaceNotePrompt, setReplaceNotePrompt] = useState(null);

  const computedPhases = buildPhaseDates(phases, jobInfo.startDate, jobInfo.hoursPerDay);
  const totalDays = computedPhases.reduce((a,p) => a+p.daysNeeded, 0);
  const totalHours = phases.reduce((a,p) => a+p.labourHours, 0);

  const updatePhase = (id, key, val) => setPhases(ps => ps.map(p => p.id===id ? {...p,[key]:val} : p));
  const removePhase = (id) => setPhases(ps => ps.filter(p => p.id!==id));
  const addPhase = () => setPhases(ps => [...ps, {
    id:`ph_new_${Date.now()}`, title:"Nouvelle phase", labourHours:8,
    crewSize:defaultCrewSize,
    showEquipment:true, showMaterials:true, showLabour:true,
    equipment:[], materials:[], labourItems:[],
  }]);
  const mergePhases = () => {
    if (mergeSelected.length < 2) return;
    const selected = phases.filter(p => mergeSelected.includes(p.id));
    const others   = phases.filter(p => !mergeSelected.includes(p.id));
    const firstIdx = phases.findIndex(p => p.id===mergeSelected[0]);
    const merged = {
      id: `ph_merged_${Date.now()}`,
      title: selected.map(p=>p.title).join(" + "),
      labourHours: selected.reduce((a,p)=>a+p.labourHours,0),
      crewSize: selected[0].crewSize,
      showEquipment:true, showMaterials:true, showLabour:true,
      equipment: selected.flatMap(p=>p.equipment),
      materials: selected.flatMap(p=>p.materials),
      labourItems: selected.flatMap(p=>p.labourItems),
    };
    const newPhases = [...others];
    newPhases.splice(firstIdx, 0, merged);
    setPhases(newPhases);
    setMergeSelected([]);
  };

  const handleSave = async () => {
    setSaving(true);
    const totalJobDays = totalDays || 1;
    const jobEndDate = addWorkdays(jobInfo.startDate, totalJobDays - 1);
    const cust = customers.find(c => c.id === jobInfo.customerId);
    const job = {
      ...jobInfo,
      crewId: jobInfo.crewId || null,
      estimateId: estimate.id,
      status: "inProgress",
      endDate: jobEndDate,
      totalDays: totalJobDays,
      totalHours,
      phases: computedPhases,
      ...(cust ? {siteAddress:{street:cust.address||"",city:cust.city||"",province:cust.province||"",postal:cust.postal||""}} : {}),
    };
    const ref = await onSave(job);
    const newId = ref?.id;
    if (newId && jobInfo.customerId && jobInfo.startDate && jobEndDate) {
      const matching = (scheduleNotes||[]).filter(n =>
        !n.replacedByJobId &&
        n.customerId === jobInfo.customerId &&
        n.startDate <= jobEndDate &&
        n.endDate >= jobInfo.startDate
      );
      if (matching.length > 0) {
        setSaving(false);
        setReplaceNotePrompt({ notes: matching, jobId: newId });
        return;
      }
    }
    setSaving(false);
    onClose();
  };

  return (
    <>
    <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-lg" style={{maxHeight:"90vh",overflow:"hidden",display:"flex",flexDirection:"column"}}>
        <div className="modal-header">
          <div>
            <div className="modal-title" style={{display:"flex",alignItems:"center",gap:8}}>
              <Icon name="jobs" size={16}/>Créer un travail
            </div>
            <div className="modal-sub">{estimate.title} · {customer?.firstName} {customer?.lastName}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            {/* Step indicator */}
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              {[1,2,3].map(s=>(
                <div key={s} style={{
                  width:24,height:24,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:11,fontWeight:700,cursor:s<step?"pointer":"default",
                  background:step===s?"var(--red)":s<step?"#dcfce7":"var(--border)",
                  color:step===s?"white":s<step?"#166534":"var(--text-3)",
                }} onClick={()=>s<step&&setStep(s)}>{s<step?"✓":s}</div>
              ))}
            </div>
            <button className="btn-icon" title={t.common.close} onClick={onClose}><Icon name="x"/></button>
          </div>
        </div>

        <div className="modal-body" style={{overflowY:"auto",flex:1}}>

          {/* ── Step 1: Job Info ── */}
          {step===1&&(
            <div className="form-grid form-grid-2">
              <div className="form-group" style={{gridColumn:"1/-1"}}>
                <label>Titre du travail *</label>
                <input className="form-input" value={jobInfo.title} onChange={e=>setJobInfo(p=>({...p,title:e.target.value}))}/>
              </div>
              <div className="form-group">
                <label>Client</label>
                <select className="form-select" value={jobInfo.customerId} onChange={e=>setJobInfo(p=>({...p,customerId:e.target.value}))}>
                  <option value="">—</option>
                  {customers.map(c=><option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Date de début *</label>
                <input className="form-input" type="date" value={jobInfo.startDate} onChange={e=>setJobInfo(p=>({...p,startDate:e.target.value}))}/>
              </div>
              <div className="form-group">
                <label>Heures de travail par jour</label>
                <input className="form-input" type="number" min={1} max={16} value={jobInfo.hoursPerDay} onChange={e=>setJobInfo(p=>({...p,hoursPerDay:+e.target.value||8}))}/>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea className="form-textarea" value={jobInfo.notes} onChange={e=>setJobInfo(p=>({...p,notes:e.target.value}))}/>
              </div>
              <div className="form-group">
                <label>{t.jobs.assignedCrew}</label>
                <select className="form-select" value={jobInfo.crewId||""} onChange={e=>setJobInfo(p=>({...p,crewId:e.target.value||null}))}>
                  <option value="">— Aucune équipe —</option>
                  {(crews||[]).map(c=>(
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{gridColumn:"1/-1"}}>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                  <input type="checkbox" checked={!!jobInfo.fixedDate} onChange={e=>setJobInfo(p=>({...p,fixedDate:e.target.checked}))}/>
                  {t.jobs.fixedDate}
                </label>
              </div>
            </div>
          )}

          {/* ── Step 2: Phases ── */}
          {step===2&&(
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                <div style={{fontSize:13,color:"var(--text-2)"}}>
                  {phases.length} phases · {totalHours} hrs total · {totalDays} jours ouvrables estimés
                </div>
                <div style={{display:"flex",gap:6}}>
                  {mergeSelected.length>=2&&(
                    <button className="btn btn-secondary btn-sm" onClick={mergePhases}>
                      <Icon name="plus" size={12}/>Fusionner ({mergeSelected.length})
                    </button>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={addPhase}>
                    <Icon name="plus" size={12}/>Ajouter phase
                  </button>
                </div>
              </div>

              {phases.map((ph,i)=>(
                <div key={ph.id} style={{
                  border:`2px solid ${mergeSelected.includes(ph.id)?"var(--red)":"var(--border)"}`,
                  borderRadius:"var(--radius-sm)",marginBottom:12,overflow:"hidden",
                }}>
                  {/* Phase header */}
                  <div style={{background:"var(--bg)",padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
                    <input
                      type="checkbox"
                      checked={mergeSelected.includes(ph.id)}
                      onChange={e=>setMergeSelected(prev=>e.target.checked?[...prev,ph.id]:prev.filter(x=>x!==ph.id))}
                      style={{cursor:"pointer"}}
                    />
                    <div style={{width:10,height:10,borderRadius:"50%",background:jobColor(i),flexShrink:0}}/>
                    <input
                      className="form-input"
                      style={{flex:1,fontWeight:600,border:"none",background:"transparent",padding:"0 4px"}}
                      value={ph.title}
                      onChange={e=>updatePhase(ph.id,"title",e.target.value)}
                    />
                    <button className="btn-icon danger" title={t.common.delete} onClick={()=>removePhase(ph.id)}><Icon name="trash" size={13}/></button>
                  </div>

                  {/* Phase details */}
                  <div style={{padding:"12px 14px",display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                    <div className="form-group" style={{margin:0}}>
                      <label style={{fontSize:10}}>Heures M/O</label>
                      <input className="form-input" type="number" min={0} value={ph.labourHours}
                        onChange={e=>updatePhase(ph.id,"labourHours",+e.target.value||0)}/>
                    </div>
                    <div className="form-group" style={{margin:0}}>
                      <label style={{fontSize:10}}>Taille équipe</label>
                      <input className="form-input" type="number" min={1} max={20} value={ph.crewSize}
                        onChange={e=>updatePhase(ph.id,"crewSize",+e.target.value||1)}/>
                    </div>
                    <div className="form-group" style={{margin:0}}>
                      <label style={{fontSize:10}}>Jours estimés</label>
                      <div style={{padding:"8px 10px",background:"var(--bg)",borderRadius:"var(--radius-sm)",fontSize:13,fontWeight:700,color:"var(--red)"}}>
                        {calcPhaseDate(jobInfo.startDate,jobInfo.hoursPerDay,ph.crewSize,ph.labourHours)} jour{calcPhaseDate(jobInfo.startDate,jobInfo.hoursPerDay,ph.crewSize,ph.labourHours)!==1?"s":""}
                      </div>
                    </div>
                  </div>

                  {/* Toggles */}
                  <div style={{padding:"0 14px 12px",display:"flex",gap:12,flexWrap:"wrap"}}>
                    {[
                      ["showLabour",`M/O (${ph.labourItems.length})`,"#3b82f6"],
                      ["showEquipment",`Équipement (${ph.equipment.length})`,"#f59e0b"],
                      ["showMaterials",`Matériaux (${ph.materials.length})`,"#10b981"],
                    ].map(([key,label,color])=>(
                      <label key={key} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,cursor:"pointer",color:ph[key]?color:"var(--text-3)"}}>
                        <input type="checkbox" checked={ph[key]} onChange={e=>updatePhase(ph.id,key,e.target.checked)}/>
                        {label}
                      </label>
                    ))}
                  </div>

                  {/* Labour items preview */}
                  {ph.showLabour&&ph.labourItems.length>0&&(
                    <div style={{padding:"0 14px 10px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#3b82f6",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>Main-d'œuvre</div>
                      {ph.labourItems.map((it,j)=>(
                        <div key={j} style={{fontSize:12,color:"var(--text-2)",display:"flex",gap:8}}>
                          <span>{it.name}</span><span style={{color:"var(--text-3)"}}>{it.qty} {it.unit}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {ph.showEquipment&&ph.equipment.length>0&&(
                    <div style={{padding:"0 14px 10px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#f59e0b",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>Équipement</div>
                      {ph.equipment.map((it,j)=>(
                        <div key={j} style={{fontSize:12,color:"var(--text-2)",display:"flex",gap:8}}>
                          <span>{it.name}</span><span style={{color:"var(--text-3)"}}>{it.qty} {it.unit}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {ph.showMaterials&&ph.materials.length>0&&(
                    <div style={{padding:"0 14px 10px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#10b981",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>Matériaux</div>
                      {ph.materials.map((it,j)=>(
                        <div key={j} style={{fontSize:12,color:"var(--text-2)",display:"flex",gap:8}}>
                          <span>{it.name}</span><span style={{color:"var(--text-3)"}}>{it.qty} {it.unit}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Step 3: Review ── */}
          {step===3&&(
            <div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:20}}>
                {(()=>{
                  const _totalJobDays=totalDays||1;
                  const _jobEndDate=addWorkdays(jobInfo.startDate,_totalJobDays-1);
                  const selectedCrew=(crews||[]).find(c=>c.id===jobInfo.crewId);
                  return [
                    ["Début",jobInfo.startDate],
                    ["Fin estimée",_jobEndDate],
                    ["Jours ouvrables",`${_totalJobDays} jours`],
                    ["Heures M/O totales",`${totalHours} hrs`],
                    ["Phases",`${phases.length} phases`],
                    ["Client",`${customers.find(c=>c.id===jobInfo.customerId)?.firstName||""} ${customers.find(c=>c.id===jobInfo.customerId)?.lastName||""}`],
                    ["Équipe", selectedCrew?.name||"—"],
                  ];
                })().map(([label,val])=>(
                  <div key={label} style={{padding:"12px 14px",background:"var(--bg)",borderRadius:"var(--radius-sm)"}}>
                    <div style={{fontSize:10,color:"var(--text-3)",fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{label}</div>
                    <div style={{fontSize:15,fontWeight:700,color:"var(--text)"}}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Timeline preview */}
              <div style={{fontWeight:600,fontSize:13,marginBottom:10}}>Calendrier des phases</div>
              {computedPhases.map((ph,i)=>(
                <div key={ph.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,padding:"10px 14px",background:"var(--bg)",borderRadius:"var(--radius-sm)",borderLeft:`4px solid ${jobColor(i)}`}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:13}}>{ph.title}</div>
                    <div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>
                      {ph.startDate} → {ph.endDate} · {ph.daysNeeded} jour{ph.daysNeeded!==1?"s":""} · {ph.labourHours} hrs · {ph.crewSize} pers.
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step>1&&<button className="btn btn-secondary" onClick={()=>setStep(s=>s-1)}>← Retour</button>}
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          {step<3
            ? <button className="btn btn-primary" onClick={()=>setStep(s=>s+1)} disabled={!jobInfo.title||!jobInfo.startDate||phases.length===0}>
                Suivant →
              </button>
            : <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                <Icon name="jobs" size={14}/>{saving?"Création...":"Créer le travail"}
              </button>
          }
        </div>
      </div>
    </div>
    {replaceNotePrompt&&(
      <div className="modal-backdrop" style={{zIndex:300}} onClick={e=>e.target===e.currentTarget&&(setReplaceNotePrompt(null),onClose())}>
        <div className="modal" style={{maxWidth:440}}>
          <div className="modal-header"><div className="modal-title">📌 {t.schedule.replaceNoteTitle}</div><button className="btn-icon" title={t.common.close} onClick={()=>{setReplaceNotePrompt(null);onClose();}}><Icon name="x"/></button></div>
          <div className="modal-body">
            <p style={{fontSize:14,color:"var(--text-2)",marginBottom:12}}>{t.schedule.replaceNoteMsg}</p>
            {replaceNotePrompt.notes.map(n=>(
              <div key={n.id} style={{padding:"8px 10px",background:"var(--bg)",borderRadius:"var(--radius-sm)",marginBottom:6,fontSize:13,borderLeft:`3px solid ${n.color||"#1d4ed8"}`}}>
                <strong>{n.title}</strong> · {n.startDate} → {n.endDate}
              </div>
            ))}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={()=>{setReplaceNotePrompt(null);onClose();}}>Non</button>
            <button className="btn btn-primary" onClick={async()=>{for(const n of replaceNotePrompt.notes)await updateScheduleNote(n.id,{...n,replacedByJobId:replaceNotePrompt.jobId});setReplaceNotePrompt(null);onClose();}}>{t.schedule.replaceNoteConfirm}</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ─── JOURNAL DE CHANTIER ──────────────────────────────────────────────────────
function JournalDeChantier({ job, role, myName, lang }) {
  const [logs, addLog, , deleteLog] = useProductionLogs(job.id);
  const [truckEvents, setTruckEvents] = useState([]);
  const [settings] = useSettings();

  // ── Shift summary form state ──
  const [showLogForm, setShowLogForm] = useState(false);
  const [logForm, setLogForm] = useState({
    phaseId: PHASE_CODES[0].code,
    phaseName: PHASE_CODES[0].labelFr,
    quantity: "",
    unit: "m³",
    shift: "journée",
    notes: "",
  });
  const [logPending, setLogPending] = useState(false);

  const canEdit = role === "admin" || role === "foreman";
  const todayStr = new Date().toISOString().slice(0, 10);
  const UNITS = ["m³", "tonne", "m linéaire", "pi²", "charge", "unité"];

  const handlePrintReport = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(generateTruckReportHTML(job, truckEvents, todayStr, settings));
    w.document.close();
  };

  const handleAddLog = async () => {
    if (!canEdit || logPending || !logForm.quantity) return;
    setLogPending(true);
    const phase = PHASE_CODES.find(p => p.code === logForm.phaseId);
    await addLog({
      jobId: job.id,
      date: todayStr,
      phaseId: logForm.phaseId,
      phaseName: phase?.labelFr || logForm.phaseName || "—",
      quantity: parseFloat(logForm.quantity) || 0,
      unit: logForm.unit,
      shift: logForm.shift,
      notes: logForm.notes,
      enteredBy: myName,
    });
    setLogForm(f => ({ ...f, quantity: "", notes: "" }));
    setShowLogForm(false);
    setLogPending(false);
  };

  // Group logs by date for display
  const logsByDate = {};
  logs.forEach(l => {
    if (!logsByDate[l.date]) logsByDate[l.date] = [];
    logsByDate[l.date].push(l);
  });
  const logDates = Object.keys(logsByDate).sort((a, b) => b.localeCompare(a));

  return (
    <div style={{ padding: "4px 0" }}>

      {/* ── Truck voyage log ── */}
      <TruckLog
        jobId={job.id}
        jobTitle={job.title}
        myName={myName}
        canEdit={canEdit}
        lang={lang}
        onEventsUpdate={setTruckEvents}
      />

      {/* ── SECTION 2: Shift Summary ── */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 12, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: 1 }}>
            Entrées de production
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={handlePrintReport}>
              <Icon name="printer" size={12} /> {lang === "en" ? "Daily report" : "Rapport du jour"}
            </button>
            {canEdit && !showLogForm && (
              <button className="btn btn-secondary btn-sm" onClick={() => setShowLogForm(true)}>
                <Icon name="plus" size={12} /> Ajouter
              </button>
            )}
          </div>
        </div>

        {/* Inline add form */}
        {canEdit && showLogForm && (
          <div style={{ padding: "12px", background: "var(--bg)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              <select
                className="form-select"
                style={{ flex: 2, minWidth: 140, fontSize: 12 }}
                value={logForm.phaseId}
                onChange={e => {
                  const ph = PHASE_CODES.find(p => p.code === e.target.value);
                  setLogForm(f => ({ ...f, phaseId: e.target.value, phaseName: ph?.labelFr || "" }));
                }}
              >
                {PHASE_CODES.map(p => (
                  <option key={p.code} value={p.code}>{p.labelFr}</option>
                ))}
              </select>
              <select
                className="form-select"
                style={{ flex: 1, minWidth: 80, fontSize: 12 }}
                value={logForm.shift}
                onChange={e => setLogForm(f => ({ ...f, shift: e.target.value }))}
              >
                <option value="matin">Matin</option>
                <option value="après-midi">Après-midi</option>
                <option value="journée">Journée</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input
                className="form-input"
                type="number"
                placeholder="Quantité"
                style={{ flex: 1, fontSize: 12 }}
                value={logForm.quantity}
                onChange={e => setLogForm(f => ({ ...f, quantity: e.target.value }))}
              />
              <select
                className="form-select"
                style={{ flex: 1, fontSize: 12 }}
                value={logForm.unit}
                onChange={e => setLogForm(f => ({ ...f, unit: e.target.value }))}
              >
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <input
              className="form-input"
              placeholder="Notes (optionnel)"
              style={{ width: "100%", fontSize: 12, marginBottom: 8 }}
              value={logForm.notes}
              onChange={e => setLogForm(f => ({ ...f, notes: e.target.value }))}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowLogForm(false)}>Annuler</button>
              <button
                className="btn btn-primary btn-sm"
                disabled={logPending || !logForm.quantity}
                onClick={handleAddLog}
              >
                {logPending ? "…" : "Enregistrer"}
              </button>
            </div>
          </div>
        )}

        {/* Logs grouped by date */}
        {logDates.length === 0 && !showLogForm && (
          <div style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center", padding: "10px 0" }}>
            Aucune entrée de production
          </div>
        )}

        {logDates.map(date => {
          const dayLogs = logsByDate[date];
          // Group by phaseId for totals
          const phaseMap = {};
          dayLogs.forEach(l => {
            const key = l.phaseId;
            if (!phaseMap[key]) phaseMap[key] = { name: l.phaseName, unit: l.unit, total: 0 };
            phaseMap[key].total += l.quantity || 0;
          });

          return (
            <div key={date} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                {new Date(date + "T12:00:00").toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long" })}
              </div>
              {/* Phase totals summary */}
              {Object.values(phaseMap).map((ph, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontWeight: 600 }}>{ph.name}</span>
                  <span style={{ color: "var(--red)", fontWeight: 700 }}>{ph.total} {ph.unit}</span>
                </div>
              ))}
              {/* Individual entries */}
              <div style={{ marginTop: 4 }}>
                {dayLogs.map(l => (
                  <div key={l.id} style={{ display: "flex", gap: 8, fontSize: 11, color: "var(--text-3)", padding: "3px 4px", alignItems: "center" }}>
                    <span style={{ minWidth: 70 }}>{l.shift}</span>
                    <span style={{ flex: 1 }}>{l.phaseName}</span>
                    <span style={{ fontWeight: 600, color: "var(--text-2)" }}>{l.quantity} {l.unit}</span>
                    <span>{l.enteredBy}</span>
                    {l.notes && <span style={{ fontStyle: "italic" }}>· {l.notes}</span>}
                    {role === "admin" && (
                      <button className="btn-icon danger" title="Supprimer" onClick={() => deleteLog(l.id)} style={{ padding: 2 }}>
                        <Icon name="trash" size={11} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── JOB COMPLETION MODAL ─────────────────────────────────────────────────────
function JobCompletionModal({ job, timesheets, invoices, lang, onConfirm, onCancel }) {
  const [photos] = usePhotos("job", job.id);
  const [logs] = useProductionLogs(job.id);
  const [notes, setNotes] = useState("");
  const [acknowledged, setAcknowledged] = useState(new Set());

  const fr = lang === "fr";

  // Check 1 (BLOCKER): all completed timesheets for this job are approved
  const jobTs = (timesheets||[]).filter(ts=>ts.jobId===job.id&&ts.punchOut);
  const unapprovedTs = jobTs.filter(ts=>ts.status!=="approved");
  const check1pass = unapprovedTs.length===0;
  const check1msg = !check1pass ? `${unapprovedTs.length} ${fr?"feuille(s) de temps non approuvée(s)":"unapproved timesheet(s)"}` : null;

  // Check 2 (BLOCKER): invoice exists and isPublished
  const linkedInvoices = (invoices||[]).filter(inv=>job.estimateId&&inv.estimateId===job.estimateId);
  const publishedInv = linkedInvoices.find(inv=>inv.isPublished===true);
  const check2pass = !job.estimateId || (linkedInvoices.length>0 && !!publishedInv);
  const check2msg = !check2pass
    ? linkedInvoices.length===0
      ? (fr?"Aucune facture liée":"No linked invoice")
      : (fr?"Facture non publiée sur le portail":"Invoice not published to portal")
    : null;

  // Check 3 (WARNING): at least one completion photo
  const completionPhotos = (photos||[]).filter(p=>p.category==="completion");
  const check3pass = completionPhotos.length>0;

  // Check 4 (WARNING): at least one production log entry
  const check4pass = (logs||[]).length>0;

  // Check 5 (WARNING): punch list all clear
  const openItems = (job.punchList||[]).filter(x=>!x.completed);
  const check5pass = openItems.length===0;
  const check5msg = !check5pass ? `${openItems.length} ${fr?"article(s) non complété(s)":"unchecked item(s)"}` : null;

  const warnings = [
    {id:"photo",  pass:check3pass, label:fr?"Photo de fin de travaux":"Completion photo",   msg:fr?"Aucune photo de fin de travaux":"No completion photo"},
    {id:"logs",   pass:check4pass, label:fr?"Journal de production":"Production log",        msg:fr?"Aucune entrée de production":"No production entries"},
    {id:"punch",  pass:check5pass, label:fr?"Liste de vérification":"Punch list",            msg:check5msg},
  ];

  const blockersPassed = check1pass && check2pass;
  const failedWarnings = warnings.filter(w=>!w.pass);
  const warningsClear = failedWarnings.every(w=>acknowledged.has(w.id));
  const canConfirm = blockersPassed && warningsClear;

  const toggleAck = (id) => setAcknowledged(prev=>{
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const renderRow = (pass, label, msg, isBlocker, warnId) => (
    <div key={label} style={{padding:"10px 12px",background:pass?"#f0fdf4":isBlocker?"#fef2f2":"#fffbeb",borderRadius:6,marginBottom:8,border:`1px solid ${pass?"#bbf7d0":isBlocker?"#fecaca":"#fde68a"}`}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:16,flexShrink:0}}>{pass?"✅":isBlocker?"🔴":"⚠️"}</span>
        <div style={{flex:1}}>
          <div style={{fontWeight:600,fontSize:13,color:pass?"#166534":isBlocker?"#991b1b":"#92400e"}}>{label}</div>
          {!pass&&msg&&<div style={{fontSize:12,color:pass?"#166534":isBlocker?"#991b1b":"#92400e",marginTop:2,opacity:0.85}}>{msg}</div>}
        </div>
        {!pass&&!isBlocker&&(
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#92400e",cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
            <input type="checkbox" checked={acknowledged.has(warnId)} onChange={()=>toggleAck(warnId)}/>
            {fr?"J'ai pris note":"I acknowledge"}
          </label>
        )}
      </div>
    </div>
  );

  return (
    <div className="modal-backdrop" style={{zIndex:300}}>
      <div className="modal" style={{maxWidth:480}}>
        <div className="modal-header">
          <div className="modal-title">{fr?"Clôturer le chantier":"Close Job"}</div>
          <button className="btn-icon" onClick={onCancel}><Icon name="x"/></button>
        </div>
        <div className="modal-body">
          <div style={{fontSize:11,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>{fr?"Bloquants":"Blockers"}</div>
          {renderRow(check1pass, fr?"Feuilles de temps approuvées":"Timesheets approved", check1msg, true, null)}
          {renderRow(check2pass, fr?"Facture publiée sur le portail":"Invoice published to portal", check2msg, true, null)}
          <div style={{fontSize:11,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8,marginTop:12}}>{fr?"Avertissements":"Warnings"}</div>
          {warnings.map(w=>renderRow(w.pass, w.label, w.msg, false, w.id))}
          <div className="form-group" style={{marginTop:16}}>
            <label>{fr?"Notes de clôture":"Completion notes"}</label>
            <textarea className="form-textarea" value={notes} onChange={e=>setNotes(e.target.value)} placeholder={fr?"Notes finales, observations...":"Final notes, observations..."} style={{minHeight:72}}/>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>{fr?"Annuler":"Cancel"}</button>
          <button className="btn btn-primary" disabled={!canConfirm} title={!canConfirm?(fr?"Corrigez les bloquants avant de continuer":"Fix blockers before continuing"):""} onClick={()=>onConfirm(notes)}>
            {fr?"Clôturer":"Close Job"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── JOBS ─────────────────────────────────────────────────────────────────────
function Jobs({ t, lang, jobs, addJob, updateJob, deleteJob, customers, teamMembers, crews, timesheets, invoices, scheduleNotes, updateScheduleNote, pendingOpenId, onPendingConsumed, role, myName }) {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDrawerTab, setJobDrawerTab] = useState("info");
  const [replaceNotePrompt, setReplaceNotePrompt] = useState(null);
  const onPendingConsumedRef = useRef(onPendingConsumed);
  useEffect(() => { onPendingConsumedRef.current = onPendingConsumed; });
  useEffect(()=>{ if(pendingOpenId){const j=jobs.find(x=>x.id===pendingOpenId);if(j){setSelectedJob(j);if(onPendingConsumedRef.current)onPendingConsumedRef.current();}} },[pendingOpenId,jobs]);
  const todayJ = new Date().toISOString().split("T")[0];
  const [punchInput, setPunchInput] = useState("");
  const [punchSaving, setPunchSaving] = useState(false);
  const [completionModal, setCompletionModal] = useState(null);

  const addPunchItem = async () => {
    if (!punchInput.trim() || !selectedJob) return;
    setPunchSaving(true);
    const item = { id: Date.now(), description: punchInput.trim(), addedBy: myName, addedAt: todayJ, completed: false, completedBy: null, completedAt: null };
    const newList = [...(selectedJob.punchList||[]), item];
    const updated = {...selectedJob, punchList: newList};
    await updateJob(selectedJob.id, updated);
    setSelectedJob(updated);
    setPunchInput("");
    setPunchSaving(false);
  };

  const togglePunchItem = async (itemId) => {
    const list = (selectedJob.punchList||[]).map(item =>
      item.id === itemId
        ? item.completed
          ? {...item, completed:false, completedBy:null, completedAt:null}
          : {...item, completed:true, completedBy:myName, completedAt:todayJ}
        : item
    );
    const updated = {...selectedJob, punchList: list};
    await updateJob(selectedJob.id, updated);
    setSelectedJob(updated);
  };

  const deletePunchItem = async (itemId) => {
    const list = (selectedJob.punchList||[]).filter(x=>x.id!==itemId);
    const updated = {...selectedJob, punchList: list};
    await updateJob(selectedJob.id, updated);
    setSelectedJob(updated);
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === "completed" && (role==="admin"||role==="foreman")) {
      setCompletionModal({job: selectedJob});
    } else {
      const updated = {...selectedJob, status: newStatus};
      await updateJob(selectedJob.id, updated);
      setSelectedJob(updated);
    }
  };

  const handleCompletionConfirm = async (notes) => {
    const updated = {...completionModal.job, status:"completed", completionNotes:notes, completedAt:todayJ};
    await updateJob(completionModal.job.id, updated);
    setSelectedJob(updated);
    setCompletionModal(null);
  };

  const filtered = jobs.filter(j=>j.title?.toLowerCase().includes(search.toLowerCase()));
  const save = async () => {
    const saveCust = customers.find(c => c.id === form.customerId);
    const jobDoc = {...form, crewId: form.crewId||null, ...(saveCust ? {siteAddress:{street:saveCust.address||"",city:saveCust.city||"",province:saveCust.province||"",postal:saveCust.postal||""}} : {})};
    const ref = await addJob(jobDoc);
    const newId = ref?.id;
    if(newId&&form.customerId&&form.startDate&&form.endDate){
      const matching=(scheduleNotes||[]).filter(n=>!n.replacedByJobId&&n.customerId===form.customerId&&n.startDate<=form.endDate&&n.endDate>=form.startDate);
      if(matching.length>0){setReplaceNotePrompt({notes:matching,jobId:newId});return;}
    }
    setModal(false);
  };

  return (
    <div>
      <div className="page-header"><div className="page-header-row"><div><h1>{t.jobs.title}</h1><p>{jobs.length} {t.jobs.subtitle}</p></div><button className="btn btn-primary" onClick={()=>{setForm({title:"",customerId:"",status:"pending",startDate:todayJ,notes:"",crewId:"",fixedDate:false});setModal(true);}}><Icon name="plus" size={14}/>{t.jobs.addBtn}</button></div></div>
      <div className="page-body">
        <div className="toolbar"><div className="search-wrap"><Icon name="search"/><input className="search-input" placeholder={t.jobs.search} value={search} onChange={e=>setSearch(e.target.value)}/></div></div>
        {filtered.length===0?(
          <div className="card"><div className="empty-state"><div className="empty-icon"><Icon name="jobs" size={40}/></div><div className="empty-title">{t.jobs.noJobs}</div><div className="empty-sub">{t.jobs.noJobsSub}</div><button className="btn btn-primary" onClick={()=>{setForm({title:"",customerId:"",status:"pending",startDate:todayJ,notes:"",crewId:"",fixedDate:false});setModal(true);}}><Icon name="plus" size={14}/>{t.jobs.addBtn}</button></div></div>
        ):(
          <div className="card">{filtered.map((j,ji)=>{
            const c=customers.find(x=>x.id===j.customerId);
            return(
              <div key={j.id} className="list-row" style={{cursor:"pointer"}} onClick={()=>setSelectedJob(selectedJob?.id===j.id?null:j)}>
                <div style={{width:10,height:10,borderRadius:"50%",background:jobColor(ji),flexShrink:0,marginTop:3}}/>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,fontSize:14,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    {(j.jobTypeTag==="warranty"||j.isWarranty)&&<span>🔧</span>}
                    {j.title}
                    {j.fixedDate&&<span style={{fontSize:11}}>📌</span>}
                    {j.crewId&&(()=>{const crew=(crews||[]).find(c=>c.id===j.crewId);return crew?<span style={{fontSize:11,fontWeight:500,color:crew.color,background:crew.color+"18",border:`1px solid ${crew.color}40`,borderRadius:3,padding:"1px 5px",display:"flex",alignItems:"center",gap:3}}><span style={{width:6,height:6,borderRadius:"50%",background:crew.color,flexShrink:0,display:"inline-block"}}/>{crew.name}</span>:null;})()}
                  </div>
                  <div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>
                    {c?`${c.firstName} ${c.lastName}`:""}{j.startDate&&` · Début: ${j.startDate}`}{j.endDate&&` · Fin: ${j.endDate}`}
                    {j.phases&&<span> · {j.phases.length} phases · {j.totalHours||0} hrs</span>}
                  </div>
                </div>
                <span className={`badge ${({pending:"badge-amber",inProgress:"badge-blue",completed:"badge-green",cancelled:"badge-gray"})[j.status]||"badge-gray"}`}>{t.jobs.status[j.status]}</span>
                <div className="list-row-actions">
                  <button className="btn-icon danger" title={t.common.delete} onClick={e=>{e.stopPropagation();setConfirmDelete(j);}}><Icon name="trash" size={14}/></button>
                </div>
              </div>
            );
          })}</div>
        )}

        {/* Phase detail modal */}
        {selectedJob&&(
          <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setSelectedJob(null)}>
            <div className="modal modal-lg" style={{maxHeight:"90vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
              <div className="modal-header" style={{alignItems:"center",gap:16}}>
                <div style={{minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{selectedJob.title}</div>
                  {(()=>{const c=customers.find(x=>x.id===selectedJob.customerId);return c?<div style={{fontSize:11,color:"var(--text-3)",marginTop:1}}>{c.firstName} {c.lastName}</div>:null;})()}
                  {(role==="admin"||role==="foreman")&&(
                    <select className="form-select" style={{fontSize:11,padding:"2px 6px",marginTop:6,width:"auto"}} value={selectedJob.status||"pending"} onChange={e=>handleStatusChange(e.target.value)}>
                      {Object.entries(t.jobs.status).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                    </select>
                  )}
                  {role==="employee"&&(
                    <span className={`badge ${({pending:"badge-amber",inProgress:"badge-blue",completed:"badge-green",cancelled:"badge-gray"})[selectedJob.status]||"badge-gray"}`} style={{marginTop:6,display:"inline-block"}}>{t.jobs.status[selectedJob.status]}</span>
                  )}
                </div>
                <div style={{display:"flex",gap:2,flex:1,justifyContent:"center",flexWrap:"wrap"}}>
                  {[
                    ["info","Info"],
                    ["phases","Phases"],
                    ["heures",t.timesheets.actualHours],
                    ...((role!=="employee")?[["journal","Journal"]]:[]),
                    ["photos","Photos"],
                    ...((role==="admin"||role==="foreman")?[["punch-list",lang==="fr"?"Liste":"Punch List"]]:[]),
                  ].map(([id,label])=>(
                    <button key={id} onClick={()=>setJobDrawerTab(id)} style={{padding:"6px 14px",fontSize:12,fontWeight:600,border:"none",background:"none",cursor:"pointer",borderBottom:`2px solid ${jobDrawerTab===id?"var(--red)":"transparent"}`,color:jobDrawerTab===id?"var(--red)":"var(--text-3)"}}>
                      {label}
                    </button>
                  ))}
                </div>
                <button className="btn-icon" title={t.common.close} onClick={()=>setSelectedJob(null)}><Icon name="x"/></button>
              </div>
              <div className="modal-body" style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
                {jobDrawerTab==="info"&&(
                  <div style={{padding:"4px 0"}}>
                    {selectedJob.siteAddress&&(selectedJob.siteAddress.street||selectedJob.siteAddress.city)?(
                      <div style={{padding:"12px 14px",background:"var(--bg)",borderRadius:"var(--radius-sm)",marginBottom:12,borderLeft:"4px solid var(--red)"}}>
                        <div style={{fontSize:10,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4,display:"flex",alignItems:"center",gap:5}}>
                          <Icon name="mapPin" size={11}/>{lang==="fr"?"Adresse du chantier":"Site Address"}
                        </div>
                        <div style={{fontSize:14,fontWeight:600,color:"var(--text)"}}>
                          {[selectedJob.siteAddress.street,selectedJob.siteAddress.city,selectedJob.siteAddress.province,selectedJob.siteAddress.postal].filter(Boolean).join(", ")}
                        </div>
                      </div>
                    ):(
                      <div style={{padding:"10px 14px",background:"var(--bg)",borderRadius:"var(--radius-sm)",marginBottom:12,fontSize:12,color:"var(--text-3)",borderLeft:"4px solid var(--border)",display:"flex",alignItems:"center",gap:6}}>
                        <Icon name="mapPin" size={11}/>{lang==="fr"?"Adresse du chantier non renseignée":"No site address on file"}
                      </div>
                    )}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                      {(()=>{const c=customers.find(x=>x.id===selectedJob.customerId); return c?(
                        <div key="cust" style={{padding:"10px 12px",background:"var(--bg)",borderRadius:"var(--radius-sm)"}}>
                          <div style={{fontSize:10,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{t.customers.title}</div>
                          <div style={{fontSize:13,fontWeight:600}}>{c.firstName} {c.lastName}</div>
                          {c.company&&<div style={{fontSize:11,color:"var(--text-3)",marginTop:1}}>{c.company}</div>}
                        </div>
                      ):null;})()}
                      <div style={{padding:"10px 12px",background:"var(--bg)",borderRadius:"var(--radius-sm)"}}>
                        <div style={{fontSize:10,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>Statut</div>
                        <span className={`badge ${({pending:"badge-amber",inProgress:"badge-blue",completed:"badge-green",cancelled:"badge-gray"})[selectedJob.status]||"badge-gray"}`}>{t.jobs.status[selectedJob.status]}</span>
                      </div>
                      {selectedJob.startDate&&(
                        <div style={{padding:"10px 12px",background:"var(--bg)",borderRadius:"var(--radius-sm)"}}>
                          <div style={{fontSize:10,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{lang==="fr"?"Période":"Period"}</div>
                          <div style={{fontSize:13,fontWeight:600}}>{selectedJob.startDate}{selectedJob.endDate?` → ${selectedJob.endDate}`:""}</div>
                        </div>
                      )}
                      {selectedJob.crewId&&(()=>{const crew=(crews||[]).find(c=>c.id===selectedJob.crewId); return crew?(
                        <div key="crew" style={{padding:"10px 12px",background:"var(--bg)",borderRadius:"var(--radius-sm)"}}>
                          <div style={{fontSize:10,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{t.jobs.assignedCrew}</div>
                          <div style={{fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                            <span style={{width:8,height:8,borderRadius:"50%",background:crew.color,flexShrink:0,display:"inline-block"}}/>
                            {crew.name}
                          </div>
                        </div>
                      ):null;})()}
                      {selectedJob.phases?.length>0&&(
                        <div style={{padding:"10px 12px",background:"var(--bg)",borderRadius:"var(--radius-sm)"}}>
                          <div style={{fontSize:10,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>Phases</div>
                          <div style={{fontSize:13,fontWeight:600}}>{selectedJob.phases.length} · {selectedJob.totalHours||0} hrs</div>
                        </div>
                      )}
                    </div>
                    {selectedJob.notes&&(
                      <div style={{marginTop:10,padding:"10px 12px",background:"var(--bg)",borderRadius:"var(--radius-sm)"}}>
                        <div style={{fontSize:10,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>Notes</div>
                        <div style={{fontSize:13,whiteSpace:"pre-wrap",color:"var(--text-2)"}}>{selectedJob.notes}</div>
                      </div>
                    )}
                  </div>
                )}
                {jobDrawerTab==="phases"&&!(selectedJob.phases||[]).length&&<div style={{fontSize:13,color:"var(--text-3)",textAlign:"center",padding:"20px 0"}}>{lang==="fr"?"Aucune phase":"No phases"}</div>}
                {jobDrawerTab==="phases"&&(selectedJob.phases||[]).map((ph,i)=>(
                  <div key={ph.id} style={{marginBottom:12,padding:"12px 14px",border:`2px solid ${jobColor(i)}22`,borderLeft:`4px solid ${jobColor(i)}`,borderRadius:"var(--radius-sm)"}}>
                    <div style={{fontWeight:700,fontSize:13,marginBottom:4,display:"flex",justifyContent:"space-between"}}>
                      <span>{ph.title}</span>
                      <span style={{fontSize:12,color:"var(--text-3)",fontWeight:400}}>{ph.startDate} → {ph.endDate}</span>
                    </div>
                    <div style={{display:"flex",gap:16,fontSize:12,color:"var(--text-3)",flexWrap:"wrap"}}>
                      <span>👥 {ph.crewSize} pers. · {ph.labourHours} hrs</span>
                      {ph.showEquipment&&ph.equipment?.length>0&&<span>🔧 {ph.equipment.map(e=>e.name).join(", ")}</span>}
                      {ph.showMaterials&&ph.materials?.length>0&&<span>📦 {ph.materials.map(m=>m.name).join(", ")}</span>}
                    </div>
                  </div>
                ))}
                {jobDrawerTab==="journal"&&(
                  <JournalDeChantier
                    job={selectedJob}
                    role={role}
                    myName={myName}
                    lang={lang}
                  />
                )}
                {jobDrawerTab==="photos"&&(
                  <div>
                    {selectedJob.customerId&&(
                      <div style={{marginBottom:20}}>
                        <PhotoGallery
                          entityType="customer"
                          entityId={selectedJob.customerId}
                          role={role}
                          uploadedBy={myName}
                          allowedCategories={["site-visit"]}
                          readOnlyCategories={["site-visit"]}
                        />
                      </div>
                    )}
                    <PhotoGallery
                      entityType="job"
                      entityId={selectedJob.id}
                      role={role}
                      uploadedBy={myName}
                      allowedCategories={["progress","completion","issue"]}
                    />
                  </div>
                )}
                {jobDrawerTab==="heures"&&(()=>{
                  const jobTs = (timesheets||[]).filter(ts=>ts.jobId===selectedJob.id&&ts.punchOut);
                  const phases = selectedJob.phases||[];
                  if (!phases.length) return <div style={{fontSize:13,color:"var(--text-3)",textAlign:"center",padding:"20px 0"}}>{lang==="fr"?"Aucune phase":"No phases"}</div>;
                  return (
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                      <thead><tr>
                        <th style={{textAlign:"left",padding:"6px 10px",borderBottom:"1px solid var(--border)",color:"var(--text-3)",fontWeight:600}}>Phase</th>
                        <th style={{textAlign:"right",padding:"6px 10px",borderBottom:"1px solid var(--border)",color:"var(--text-3)",fontWeight:600}}>{t.timesheets.estimatedHours}</th>
                        <th style={{textAlign:"right",padding:"6px 10px",borderBottom:"1px solid var(--border)",color:"var(--text-3)",fontWeight:600}}>{t.timesheets.actualHours}</th>
                        <th style={{textAlign:"right",padding:"6px 10px",borderBottom:"1px solid var(--border)",color:"var(--text-3)",fontWeight:600}}>Écart</th>
                      </tr></thead>
                      <tbody>
                        {phases.map((ph,i)=>{
                          const actual = jobTs.flatMap(ts=>(ts.taskAllocations||[]).filter(a=>a.phaseId===ph.id)).reduce((a,r)=>a+(parseFloat(r.hours)||0),0);
                          const est = ph.labourHours||0;
                          const ratio = est>0?actual/est:null;
                          const color = ratio===null?"var(--text-3)":ratio<1?"var(--green)":ratio<1.1?"#d97706":"var(--red)";
                          return (<tr key={ph.id}>
                            <td style={{padding:"8px 10px",borderBottom:"1px solid var(--border)",fontWeight:500}}>{ph.title}</td>
                            <td style={{textAlign:"right",padding:"8px 10px",borderBottom:"1px solid var(--border)",color:"var(--text-3)"}}>{est}h</td>
                            <td style={{textAlign:"right",padding:"8px 10px",borderBottom:"1px solid var(--border)",fontWeight:600,color}}>{actual.toFixed(2)}h</td>
                            <td style={{textAlign:"right",padding:"8px 10px",borderBottom:"1px solid var(--border)",fontSize:12,color}}>{ratio!==null?(()=>{const d=Math.round((ratio-1)*100);return `${d>0?"+":""}${d}%`;})():"—"}</td>
                          </tr>);
                        })}
                        {(()=>{
                          const otherActual = jobTs.flatMap(ts=>(ts.taskAllocations||[]).filter(a=>a.phaseId==="autre"||!phases.find(p=>p.id===a.phaseId))).reduce((a,r)=>a+(parseFloat(r.hours)||0),0);
                          if(!otherActual) return null;
                          return <tr><td style={{padding:"8px 10px",fontWeight:500,color:"var(--text-3)"}}>Autre</td><td style={{textAlign:"right",padding:"8px 10px",color:"var(--text-3)"}}>—</td><td style={{textAlign:"right",padding:"8px 10px",fontWeight:600}}>{otherActual.toFixed(2)}h</td><td/></tr>;
                        })()}
                      </tbody>
                    </table>
                  );
                })()}
                {jobDrawerTab==="punch-list"&&(role==="admin"||role==="foreman")&&(()=>{
                  const punchList = selectedJob.punchList||[];
                  const open = punchList.filter(x=>!x.completed);
                  const done = punchList.filter(x=>x.completed);
                  return (
                    <div>
                      {/* Add item */}
                      <div style={{display:"flex",gap:8,marginBottom:16}}>
                        <input
                          className="form-input"
                          style={{flex:1}}
                          placeholder={lang==="fr"?"Nouvel élément...":"New item..."}
                          value={punchInput}
                          onChange={e=>setPunchInput(e.target.value)}
                          onKeyDown={e=>e.key==="Enter"&&addPunchItem()}
                        />
                        <button className="btn btn-primary btn-sm" disabled={!punchInput.trim()||punchSaving} onClick={addPunchItem}>
                          <Icon name="plus" size={13}/>{lang==="fr"?"Ajouter":"Add"}
                        </button>
                      </div>
                      {/* Open items */}
                      {open.length===0&&done.length===0&&(
                        <div style={{fontSize:13,color:"var(--text-3)",textAlign:"center",padding:"16px 0"}}>{lang==="fr"?"Aucun élément":"No items"}</div>
                      )}
                      {open.map(item=>(
                        <div key={item.id} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"9px 0",borderBottom:"1px solid var(--border)"}}>
                          <input type="checkbox" style={{marginTop:2,cursor:"pointer"}} checked={false} onChange={()=>togglePunchItem(item.id)}/>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:500}}>{item.description}</div>
                            <div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>{lang==="fr"?"Ajouté par":"Added by"} {item.addedBy} · {item.addedAt}</div>
                          </div>
                          {role==="admin"&&<button className="btn-icon danger" title={lang==="fr"?"Supprimer":"Delete"} onClick={()=>deletePunchItem(item.id)} style={{flexShrink:0}}><Icon name="trash" size={12}/></button>}
                        </div>
                      ))}
                      {/* Done items */}
                      {done.length>0&&(
                        <div style={{marginTop:16,opacity:0.5}}>
                          <div style={{fontSize:11,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>{lang==="fr"?"Complétés":"Completed"} ({done.length})</div>
                          {done.map(item=>(
                            <div key={item.id} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"7px 0",borderBottom:"1px solid var(--border)"}}>
                              <input type="checkbox" style={{marginTop:2,cursor:"pointer"}} checked={true} onChange={()=>togglePunchItem(item.id)}/>
                              <div style={{flex:1}}>
                                <div style={{fontSize:13,textDecoration:"line-through"}}>{item.description}</div>
                                <div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>{lang==="fr"?"Complété par":"Completed by"} {item.completedBy} · {item.completedAt}</div>
                              </div>
                              {role==="admin"&&<button className="btn-icon danger" title={lang==="fr"?"Supprimer":"Delete"} onClick={()=>deletePunchItem(item.id)} style={{flexShrink:0}}><Icon name="trash" size={12}/></button>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
      {modal&&(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-header"><div className="modal-title">{t.jobs.addBtn}</div><button className="btn-icon" title={t.common.close} onClick={()=>setModal(false)}><Icon name="x"/></button></div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group" style={{gridColumn:"1/-1"}}><label>Titre *</label><input className="form-input" value={form.title||""} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/></div>
                <div className="form-group"><label>{t.customers.title}</label><select className="form-select" value={form.customerId||""} onChange={e=>setForm(p=>({...p,customerId:e.target.value}))}><option value="">—</option>{customers.map(c=><option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}</select></div>
                <div className="form-group"><label>Statut</label><select className="form-select" value={form.status||"pending"} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>{Object.entries(t.jobs.status).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
                <div className="form-group"><label>Date début</label><input className="form-input" type="date" value={form.startDate||""} onChange={e=>setForm(p=>({...p,startDate:e.target.value}))}/></div>
                <div className="form-group" style={{gridColumn:"1/-1"}}><label>Notes</label><textarea className="form-textarea" value={form.notes||""} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/></div>
                <div className="form-group"><label>{t.estimates.jobTypeTag}</label><select className="form-select" value={form.jobTypeTag||""} onChange={e=>setForm(p=>({...p,jobTypeTag:e.target.value}))}><option value="">—</option>{Object.entries(t.estimates.jobTypeTags).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
                <div className="form-group">
                  <label>{t.jobs.assignedCrew}</label>
                  <select className="form-select" value={form.crewId||""} onChange={e=>setForm(p=>({...p,crewId:e.target.value||null}))}>
                    <option value="">— Aucune équipe —</option>
                    {(crews||[]).map(c=>(
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{gridColumn:"1/-1"}}>
                  <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                    <input type="checkbox" checked={!!form.fixedDate} onChange={e=>setForm(p=>({...p,fixedDate:e.target.checked}))}/>
                    {t.jobs.fixedDate}
                  </label>
                </div>
              </div>
              {form.jobTypeTag==="warranty"&&(
                <div className="form-grid form-grid-2" style={{marginTop:12}}>
                  <div className="form-group"><label>Raison / Reason</label><select className="form-select" value={form.warrantyReason||""} onChange={e=>setForm(p=>({...p,warrantyReason:e.target.value}))}><option value="">—</option><option value="defect">Défaut d'exécution / Workmanship Defect</option><option value="material-failure">Défaillance matériau / Material Failure</option><option value="client-damage">Dommage client / Client Damage</option><option value="goodwill">Bonne volonté / Goodwill</option></select></div>
                  <div className="form-group"><label>Travail original / Original Job</label><select className="form-select" value={form.warrantyRelatedJobId||""} onChange={e=>setForm(p=>({...p,warrantyRelatedJobId:e.target.value}))}><option value="">—</option>{jobs.map(j=><option key={j.id} value={j.id}>{j.title}</option>)}</select></div>
                </div>
              )}
              <div className="divider"/>
              <div style={{fontFamily:"var(--font-display)",fontWeight:600,fontSize:13,marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
                <Icon name="mapPin" size={13}/>Localisation du chantier (GPS)
              </div>
              <div style={{fontSize:12,color:"var(--text-3)",marginBottom:10}}>Utilisé par le pointage pour vérifier que l'employé est sur place.</div>
              <div className="form-grid form-grid-2" style={{gap:10}}>
                <div className="form-group"><label>Latitude</label><input className="form-input" type="number" step="any" placeholder="45.123456" value={form.siteLocation?.lat||""} onChange={e=>setForm(p=>({...p,siteLocation:{...(p.siteLocation||{}),lat:+e.target.value||null}}))}/></div>
                <div className="form-group"><label>Longitude</label><input className="form-input" type="number" step="any" placeholder="-71.234567" value={form.siteLocation?.lng||""} onChange={e=>setForm(p=>({...p,siteLocation:{...(p.siteLocation||{}),lng:+e.target.value||null}}))}/></div>
                <div className="form-group"><label>Rayon (mètres)</label><input className="form-input" type="number" placeholder="300" value={form.siteLocation?.radiusMetres||""} onChange={e=>setForm(p=>({...p,siteLocation:{...(p.siteLocation||{}),radiusMetres:+e.target.value||300}}))}/></div>
                <div className="form-group" style={{display:"flex",alignItems:"flex-end"}}>
                  <button type="button" className="btn btn-secondary" style={{width:"100%"}} onClick={()=>navigator.geolocation.getCurrentPosition(p=>setForm(f=>({...f,siteLocation:{...f.siteLocation,lat:+p.coords.latitude.toFixed(6),lng:+p.coords.longitude.toFixed(6),radiusMetres:f.siteLocation?.radiusMetres||300}})),()=>alert("GPS non disponible"))}>
                    <Icon name="mapPin" size={13}/>Ma position actuelle
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setModal(false)}>{t.common.cancel}</button><button className="btn btn-primary" onClick={save}>{t.common.save}</button></div>
          </div>
        </div>
      )}
      {completionModal&&(
        <JobCompletionModal
          job={completionModal.job}
          timesheets={timesheets}
          invoices={invoices}
          lang={lang}
          onConfirm={handleCompletionConfirm}
          onCancel={()=>setCompletionModal(null)}
        />
      )}
      {confirmDelete&&<ConfirmDelete message={`Supprimer le travail "${confirmDelete.title}"?`} onConfirm={()=>{deleteJob(confirmDelete.id);setConfirmDelete(null);}} onCancel={()=>setConfirmDelete(null)}/>}
      {replaceNotePrompt&&(
        <div className="modal-backdrop" style={{zIndex:300}} onClick={e=>e.target===e.currentTarget&&(setReplaceNotePrompt(null),setModal(false))}>
          <div className="modal" style={{maxWidth:440}}>
            <div className="modal-header"><div className="modal-title">📌 {t.schedule.replaceNoteTitle}</div><button className="btn-icon" title={t.common.close} onClick={()=>{setReplaceNotePrompt(null);setModal(false);}}><Icon name="x"/></button></div>
            <div className="modal-body">
              <p style={{fontSize:14,color:"var(--text-2)",marginBottom:12}}>{t.schedule.replaceNoteMsg}</p>
              {replaceNotePrompt.notes.map(n=>(
                <div key={n.id} style={{padding:"8px 10px",background:"var(--bg)",borderRadius:"var(--radius-sm)",marginBottom:6,fontSize:13,borderLeft:`3px solid ${n.color||"#1d4ed8"}`}}>
                  <strong>{n.title}</strong> · {n.startDate} → {n.endDate}
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>{setReplaceNotePrompt(null);setModal(false);}}>Non</button>
              <button className="btn btn-primary" onClick={async()=>{for(const n of replaceNotePrompt.notes)await updateScheduleNote(n.id,{...n,replacedByJobId:replaceNotePrompt.jobId});setReplaceNotePrompt(null);setModal(false);}}>{t.schedule.replaceNoteConfirm}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Jobs;
